import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  Table,
  Button,
  Container,
  Row,
  Col,
  Spinner,
  Form,
} from 'react-bootstrap';

const HostsTable = ({ 
  data = [], 
  isLoading = false, 
  knownHostsMap = {}, 
  updatingHosts = new Set(),
  onToggleKnown 
}) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState([]);
  const [isTogglingAll, setIsTogglingAll] = useState(false);
  
  // Define columns
  const columnHelper = createColumnHelper();
  
  const columns = useMemo(
    () => [
      columnHelper.accessor('host', {
        header: 'Host',
        cell: (info) => (
          <span className="text-break" style={{ fontFamily: 'monospace' }}>
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('domain', {
        header: 'Domain',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('discovered', {
        header: 'Discovered',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor(
        (row) => knownHostsMap[row.host] || false,
        {
          id: 'knownCol',
          header: 'Known',
          size: 40,
          cell: (props) => {
            const { host, domain } = props.row.original;
            const isKnown = knownHostsMap[host] || false;
            const isUpdating = updatingHosts.has(host);

            return (
              <div className="d-flex align-items-center">
                <label className="custom-toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={isKnown}
                    onChange={() => !isUpdating && onToggleKnown(host, domain, isKnown)}
                    disabled={isUpdating}
                  />
                  <span className="custom-toggle-slider"></span>
                </label>
                {isUpdating && (
                  <Spinner 
                    animation="border" 
                    size="sm" 
                    className="ms-2"
                    style={{ width: '1rem', height: '1rem' }}
                  />
                )}
              </div>
            );
          },
          sortingFn: (rowA, rowB) => {
            const a = knownHostsMap[rowA.original.host] || false;
            const b = knownHostsMap[rowB.original.host] || false;
            
            // Primary sort by known status
            if (a === b) {
              // Secondary sort by host name
              return rowA.original.host.localeCompare(rowB.original.host);
            }
            return a ? 1 : -1; // false (unchecked) comes first
          },
        }
      ),
    ],
    [knownHostsMap, updatingHosts, onToggleKnown, columnHelper]
  );
  
  // Create table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  
  // Handle toggle all visible rows
  const handleToggleAll = async () => {
    const visibleRows = table.getRowModel().rows;
    
    if (visibleRows.length === 0 || isTogglingAll) return;
    
    setIsTogglingAll(true);
    
    try {
      // Determine if we should turn all ON or all OFF
      const knownCount = visibleRows.filter(row => 
        knownHostsMap[row.original.host]
      ).length;
      
      const shouldTurnOn = knownCount < (visibleRows.length / 2);
      
      console.log(`Toggle All: ${shouldTurnOn ? 'Turning ON' : 'Turning OFF'} ${visibleRows.length} hosts`);
      
      // Process rows sequentially with small delay to avoid overwhelming backend
      for (const row of visibleRows) {
        const { host, domain } = row.original;
        const currentStatus = knownHostsMap[host] || false;
        
        // Only toggle if it needs to change
        if (shouldTurnOn && !currentStatus) {
          onToggleKnown(host, domain, currentStatus);
          // Small delay between requests (100ms = 10 requests/second)
          await new Promise(resolve => setTimeout(resolve, 100));
        } else if (!shouldTurnOn && currentStatus) {
          onToggleKnown(host, domain, currentStatus);
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      console.log('Toggle All: Completed');
    } finally {
      setIsTogglingAll(false);
    }
  };
  
  if (data.length === 0) {
    return (
      <div className="text-center p-5 bg-light rounded">
        <p className="text-muted mb-0">No hosts found</p>
      </div>
    );
  }
  
  return (
    <>
      <style>{`
        /* Custom Toggle Switch Styles */
        .custom-toggle-switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
          margin: 0;
        }

        .custom-toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .custom-toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 24px;
        }

        .custom-toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        input:checked + .custom-toggle-slider {
          background-color: #1abc9c;
        }

        input:checked + .custom-toggle-slider:before {
          transform: translateX(20px);
        }

        input:disabled + .custom-toggle-slider {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .custom-toggle-switch:hover .custom-toggle-slider {
          background-color: #b3b3b3;
        }

        input:checked + .custom-toggle-slider:hover {
          background-color: #16a085;
        }
      `}</style>
      
      <Container fluid className="px-0">
        {/* Pagination controls - Top */}
        <Row style={{position: 'relative', bottom: '10px'}}>
          <Col xs={12} md={6} className="mb-2 mb-md-0"></Col>
          <Col xs={12} md={6} className="mb-2 mb-md-0">
            <div className="d-flex justify-content-md-end align-items-center">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleToggleAll}
                disabled={data.length === 0 || isTogglingAll}
                className="me-3"
              >
                {isTogglingAll ? (
                  <>
                    <Spinner 
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                    Toggling...
                  </>
                ) : (
                  'Toggle All Visible'
                )}
              </Button>
            </div>
          </Col>
        </Row>
        <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <div className="d-flex align-items-center">
            <span className="me-2 text-muted">Show &nbsp;</span>
            <Form.Select
              value={pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
              style={{ width: 'auto'}}
            >
              {[10, 25, 50, 100, 250, 500].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Form.Select>
            <span className="ms-2 text-muted">&nbsp; entries</span>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-md-end">
            <span className="me-3 align-self-center text-muted">
              Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>&nbsp;
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </Col>
      </Row>
        
        {/* Table */}
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th 
                      key={header.id}
                      style={{ 
                        width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span style={{ fontSize: '0.8em' }}>
                            {header.column.getIsSorted() === 'asc' ? '▲' : 
                            header.column.getIsSorted() === 'desc' ? '▼' : 
                            '⇅'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        
        {/* Pagination controls - Bottom */}
        <Row className="mt-3 align-items-center">
        <Col xs={12} md={6} className="mb-2 mb-md-0" Style="text-align: justify;">
          <span className="text-muted">
            Showing {table.getRowModel().rows.length} of {data.length} entries
          </span>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-md-end">
            <span className="me-3 align-self-center text-muted" Style="color:var(--dark);">
              Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="me-1"
            >
              Previous
            </Button>&nbsp;
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </Col>
      </Row>
      </Container>
    </>
  );
};

export default HostsTable;



// import React, { useState, useMemo } from 'react';
// import {
//   useReactTable,
//   getCoreRowModel,
//   flexRender,
//   getPaginationRowModel,
//   getSortedRowModel,
//   createColumnHelper,
// } from '@tanstack/react-table';
// import {
//   Table,
//   Button,
//   Container,
//   Row,
//   Col,
//   Spinner,
//   Form,
// } from 'react-bootstrap';

// const HostsTable = ({ 
//   data = [], 
//   isLoading = false, 
//   knownHostsMap = {}, 
//   updatingHosts = new Set(),
//   onToggleKnown 
// }) => {
//   const [pagination, setPagination] = useState({
//     pageIndex: 0,
//     pageSize: 20,
//   });
//   const [sorting, setSorting] = useState([]);
//   const [isTogglingAll, setIsTogglingAll] = useState(false);
  
//   // Define columns
//   const columnHelper = createColumnHelper();
  
//   const columns = useMemo(
//     () => [
//       columnHelper.accessor('host', {
//         header: 'Host',
//         cell: (info) => (
//           <span className="text-break" style={{ fontFamily: 'monospace' }}>
//             {info.getValue()}
//           </span>
//         ),
//       }),
//       columnHelper.accessor('domain', {
//         header: 'Domain',
//         cell: (info) => info.getValue() || '-',
//       }),
//       columnHelper.accessor('discovered', {
//         header: 'Discovered',
//         cell: (info) => info.getValue() || '-',
//       }),
//       columnHelper.accessor(
//         (row) => knownHostsMap[row.host] || false,
//         {
//           id: 'known',
//           header: 'Known',
//           size: 40,
//           cell: (props) => {
//             const { host, domain } = props.row.original;
//             const isKnown = knownHostsMap[host] || false;
//             const isUpdating = updatingHosts.has(host);

//             return (
//               <div className="d-flex align-items-center">
//                 <label className="custom-toggle-switch">
//                   <input 
//                     type="checkbox" 
//                     checked={isKnown}
//                     onChange={() => !isUpdating && onToggleKnown(host, domain, isKnown)}
//                     disabled={isUpdating}
//                   />
//                   <span className="custom-toggle-slider"></span>
//                 </label>
//                 {isUpdating && (
//                   <Spinner 
//                     animation="border" 
//                     size="sm" 
//                     className="ms-2"
//                     style={{ width: '1rem', height: '1rem' }}
//                   />
//                 )}
//               </div>
//             );
//           },
//           sortingFn: (rowA, rowB) => {
//             const a = knownHostsMap[rowA.original.host] || false;
//             const b = knownHostsMap[rowB.original.host] || false;
            
//             // Primary sort by known status
//             if (a === b) {
//               // Secondary sort by host name
//               return rowA.original.host.localeCompare(rowB.original.host);
//             }
//             return a ? 1 : -1; // false (unchecked) comes first
//           },
//         }
//       ),
//     ],
//     [knownHostsMap, updatingHosts, onToggleKnown, columnHelper]
//   );
  
//   // Create table instance
//   const table = useReactTable({
//     data,
//     columns,
//     state: {
//       pagination,
//       sorting,
//     },
//     onPaginationChange: setPagination,
//     onSortingChange: setSorting,
//     getCoreRowModel: getCoreRowModel(),
//     getPaginationRowModel: getPaginationRowModel(),
//     getSortedRowModel: getSortedRowModel(),
//   });
  
//   // Handle toggle all visible rows
//   const handleToggleAll = async () => {
//     const visibleRows = table.getRowModel().rows;
    
//     if (visibleRows.length === 0 || isTogglingAll) return;
    
//     setIsTogglingAll(true);
    
//     try {
//       // Determine if we should turn all ON or all OFF
//       const knownCount = visibleRows.filter(row => 
//         knownHostsMap[row.original.host]
//       ).length;
      
//       const shouldTurnOn = knownCount < (visibleRows.length / 2);
      
//       console.log(`Toggle All: ${shouldTurnOn ? 'Turning ON' : 'Turning OFF'} ${visibleRows.length} hosts`);
      
//       // Process rows sequentially with small delay to avoid overwhelming backend
//       for (const row of visibleRows) {
//         const { host, domain } = row.original;
//         const currentStatus = knownHostsMap[host] || false;
        
//         // Only toggle if it needs to change
//         if (shouldTurnOn && !currentStatus) {
//           onToggleKnown(host, domain, currentStatus);
//           // Small delay between requests (100ms = 10 requests/second)
//           await new Promise(resolve => setTimeout(resolve, 100));
//         } else if (!shouldTurnOn && currentStatus) {
//           onToggleKnown(host, domain, currentStatus);
//           await new Promise(resolve => setTimeout(resolve, 100));
//         }
//       }
      
//       console.log('Toggle All: Completed');
//     } finally {
//       setIsTogglingAll(false);
//     }
//   };
  
//   if (isLoading) {
//     return (
//       <div className="text-center p-5">
//         <Spinner animation="border" />
//         <p className="mt-2">Loading hosts...</p>
//       </div>
//     );
//   }
  
//   if (data.length === 0) {
//     return (
//       <div className="text-center p-5 bg-light rounded">
//         <p className="text-muted mb-0">No hosts found</p>
//       </div>
//     );
//   }
  
//   return (
//     <>
//       <style>{`
//         /* Custom Toggle Switch Styles */
//         .custom-toggle-switch {
//           position: relative;
//           display: inline-block;
//           width: 44px;
//           height: 24px;
//           margin: 0;
//         }

//         .custom-toggle-switch input {
//           opacity: 0;
//           width: 0;
//           height: 0;
//         }

//         .custom-toggle-slider {
//           position: absolute;
//           cursor: pointer;
//           top: 0;
//           left: 0;
//           right: 0;
//           bottom: 0;
//           background-color: #ccc;
//           transition: 0.3s;
//           border-radius: 24px;
//         }

//         .custom-toggle-slider:before {
//           position: absolute;
//           content: "";
//           height: 18px;
//           width: 18px;
//           left: 3px;
//           bottom: 3px;
//           background-color: white;
//           transition: 0.3s;
//           border-radius: 50%;
//         }

//         input:checked + .custom-toggle-slider {
//           background-color: #1abc9c;
//         }

//         input:checked + .custom-toggle-slider:before {
//           transform: translateX(20px);
//         }

//         input:disabled + .custom-toggle-slider {
//           opacity: 0.5;
//           cursor: not-allowed;
//         }

//         .custom-toggle-switch:hover .custom-toggle-slider {
//           background-color: #b3b3b3;
//         }

//         input:checked + .custom-toggle-slider:hover {
//           background-color: #16a085;
//         }
//       `}</style>
      
//       <Container fluid className="px-0">
//         {/* Pagination controls - Top */}
//         <Row style={{position: 'relative', bottom: '10px'}}>
//           <Col xs={12} md={6} className="mb-2 mb-md-0"></Col>
//           <Col xs={12} md={6} className="mb-2 mb-md-0">
//             <div className="d-flex justify-content-md-end align-items-center">
//               <Button
//                 variant="outline-primary"
//                 size="sm"
//                 onClick={handleToggleAll}
//                 disabled={data.length === 0 || isTogglingAll}
//                 className="me-3"
//               >
//                 {isTogglingAll ? (
//                   <>
//                     <Spinner 
//                       as="span"
//                       animation="border"
//                       size="sm"
//                       role="status"
//                       aria-hidden="true"
//                       className="me-1"
//                     />
//                     Toggling...
//                   </>
//                 ) : (
//                   'Toggle All Visible'
//                 )}
//               </Button>
//             </div>
//           </Col>
//         </Row>
//         <Row className="mb-3 align-items-center">
//         <Col xs={12} md={6} className="mb-2 mb-md-0">
//           <div className="d-flex align-items-center">
//             <span className="me-2 text-muted">Show &nbsp;</span>
//             <Form.Select
//               value={pagination.pageSize}
//               onChange={(e) => {
//                 table.setPageSize(Number(e.target.value));
//               }}
//               style={{ width: 'auto'}}
//             >
//               {[10, 25, 50, 100, 250, 500].map((pageSize) => (
//                 <option key={pageSize} value={pageSize}>
//                   {pageSize}
//                 </option>
//               ))}
//             </Form.Select>
//             <span className="ms-2 text-muted">&nbsp; entries</span>
//           </div>
//         </Col>
//         <Col xs={12} md={6}>
//           <div className="d-flex justify-content-md-end">
//             <span className="me-3 align-self-center text-muted">
//               Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
//             </span>
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.previousPage()}
//               disabled={!table.getCanPreviousPage()}
//             >
//               Previous
//             </Button>&nbsp;
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.nextPage()}
//               disabled={!table.getCanNextPage()}
//             >
//               Next
//             </Button>
//           </div>
//         </Col>
//       </Row>
        
//         {/* Table */}
//         <div className="table-responsive">
//           <Table striped bordered hover>
//             <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
//               {table.getHeaderGroups().map((headerGroup) => (
//                 <tr key={headerGroup.id}>
//                   {headerGroup.headers.map((header) => (
//                     <th 
//                       key={header.id}
//                       style={{ 
//                         width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
//                         textAlign: 'center',
//                         verticalAlign: 'middle',
//                         cursor: header.column.getCanSort() ? 'pointer' : 'default',
//                         userSelect: 'none',
//                       }}
//                       onClick={header.column.getToggleSortingHandler()}
//                     >
//                       <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
//                         {header.isPlaceholder
//                           ? null
//                           : flexRender(header.column.columnDef.header, header.getContext())}
//                         {header.column.getCanSort() && (
//                           <span style={{ fontSize: '0.8em' }}>
//                             {header.column.getIsSorted() === 'asc' ? '▲' : 
//                             header.column.getIsSorted() === 'desc' ? '▼' : 
//                             '⇅'}
//                           </span>
//                         )}
//                       </div>
//                     </th>
//                   ))}
//                 </tr>
//               ))}
//             </thead>
//             <tbody>
//               {table.getRowModel().rows.map((row) => (
//                 <tr key={row.id}>
//                   {row.getVisibleCells().map((cell) => (
//                     <td key={cell.id}>
//                       {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                     </td>
//                   ))}
//                 </tr>
//               ))}
//             </tbody>
//           </Table>
//         </div>
        
//         {/* Pagination controls - Bottom */}
//         <Row className="mt-3 align-items-center">
//         <Col xs={12} md={6} className="mb-2 mb-md-0" Style="text-align: justify;">
//           <span className="text-muted">
//             Showing {table.getRowModel().rows.length} of {data.length} entries
//           </span>
//         </Col>
//         <Col xs={12} md={6}>
//           <div className="d-flex justify-content-md-end">
//             <span className="me-3 align-self-center text-muted" Style="color:var(--dark);">
//               Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
//             </span>
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.previousPage()}
//               disabled={!table.getCanPreviousPage()}
//               className="me-1"
//             >
//               Previous
//             </Button>&nbsp;
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.nextPage()}
//               disabled={!table.getCanNextPage()}
//             >
//               Next
//             </Button>
//           </div>
//         </Col>
//       </Row>
//       </Container>
//     </>
//   );
// };

// export default HostsTable;